import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Caravan,
  Fish,
  Gamepad2,
  MapPin,
  Ship,
  Users,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import { FISHING_GUIDE_PATH, WINTER_TEXANS_GUIDE_PATH, routeTo } from "@/lib/site";
import { Reveal } from "../Reveal";



type Experience = {
  icon: LucideIcon;
  title: string;
  body: string;
  accent: string;
  cta?: { label: string; href: string };
};


const EXPERIENCES: Experience[] = [
  {
    icon: Fish,
    title: "Fishing Near Falcon Lake",
    body: "Zapata is closely connected to Falcon Lake, a destination known throughout the fishing community. Amigo Inn provides a convenient home base for anglers planning time on the water.",
    accent: "01",
    cta: { label: "Fishing Guide", href: FISHING_GUIDE_PATH },
  },
  {
    icon: Users,
    title: "Family Reunions and Celebrations",
    body: "Bring family and friends together for reunions, birthdays, holiday gatherings, and other meaningful occasions.",
    accent: "02",
  },
  {
    icon: Gamepad2,
    title: "Clubhouse and Indoor Recreation",
    body: "Spend time together in the recreation and game areas with shuffleboard, a poker table, a dartboard, board games, and space to relax.",
    accent: "03",
  },
  {
    icon: Utensils,
    title: "Outdoor Meals and Barbecues",
    body: "Enjoy the covered picnic area, barbecue pits, tables, and outdoor gathering space during your stay.",
    accent: "04",
  },
  {
    icon: Ship,
    title: "Boating and the Outdoors",
    body: "The park is conveniently located near public boat access, making it easier for guests to enjoy Falcon Lake and the South Texas outdoors.",
    accent: "05",
  },
  {
    icon: Caravan,
    title: "Winter Texan Stays",
    body:
      "Seasonal visitors return to Zapata year after year for mild winters, full-hookup sites, furnished cabins, and a welcoming community. Discover why Amigo Inn is a favorite Winter Texan park.",
    accent: "06",
    cta: { label: "Winter Texan Guide", href: WINTER_TEXANS_GUIDE_PATH },
  },
  {
    icon: MapPin,
    title: "South Texas Culture",
    body: "Explore the welcoming community of Zapata and experience the area's blend of Texan and Mexican traditions, local hospitality, and memorable food.",
    accent: "07",
  },
];


export function ExperienceDirectory() {
  return (
    <section className="bg-surface py-24 sm:py-28 lg:py-32" aria-labelledby="experiences-title">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-clay">Ways to Enjoy Your Stay</p>
          <h2
            id="experiences-title"
            className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
          >
            Gather, Play and Explore Around Amigo Inn
          </h2>
        </Reveal>

        <ul className="mt-14 grid gap-x-12 gap-y-0 lg:grid-cols-2">
          {EXPERIENCES.map((item, i) => (
            <Reveal as="li" key={item.title} delay={(i % 2) * 0.07} y={22}>
              <article className="group rule-sand flex gap-5 py-8 transition-transform duration-500 ease-out hover:-translate-y-0.5 lg:py-10">
                <div className="flex flex-col items-start gap-4">
                  <span
                    className="font-display text-sm tracking-[0.18em] text-sand"
                    aria-hidden="true"
                  >
                    {item.accent}
                  </span>
                  <item.icon
                    className="size-5 text-forest transition-colors duration-300 group-hover:text-clay"
                    aria-hidden="true"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-xl text-forest-deep sm:text-2xl">
                    {item.title}
                  </h3>
                  <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.body}
                  </p>
                  {item.cta ? (
                    <Link
                      to={routeTo(item.cta.href)}
                      className="link-underline mt-4 inline-flex text-sm font-semibold text-forest"
                    >
                      {item.cta.label} <ArrowRight className="size-4" aria-hidden="true" />
                    </Link>
                  ) : null}
                </div>

              </article>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
